import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  useReducer } from "react"
import isEqual from 'react-fast-compare'
import useDeepCompareEffect from 'use-deep-compare-effect'
import { useDebouncedCallback } from 'use-debounce'
import { reducerDeepCompare } from 'components/utils/useStateDeepEqual'

// Debug component renders
//import { useWhatChanged } from '@simbathesailor/use-what-changed';
//import useTilg from 'tilg'
//const shouldDebug = true

import {
  defaultValues,
  dataStore,
  mapStore,
  locationStore,
  pageStore,
  filterStore
} from "state/store-zustand";

import useContextTheme from 'components/utils/useContextTheme'
import useFetchBoundary from 'components/utils/useFetchBoundary.js'; // Adjust the import path as needed

import ReactMapGL, {
  Source,
  Marker,
  Layer,
  LinearInterpolator, // FIXME: This doesn't work with React Map GL 7.x
  NavigationControl,
  FullscreenControl,
  Popup,
  ScaleControl,
  WebMercatorViewport
} from "react-map-gl"
import "mapbox-gl/dist/mapbox-gl.css"


import mapboxgl from "mapbox-gl"; // This is a dependency of react-map-gl even if you didn't explicitly install it
// eslint-disable-next-line import/no-webpack-loader-syntax
//mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

import bbox from "@turf/bbox"
import { featureCollection } from "@turf/helpers"
import {
  compareLists,
  decodeFeature,
  featuresToBounds,
  flatBoundsFromBounds,
  getRoute,
  getIconSizeFromZoom,
  getTopMarkerClusters,
  locationFromGeometry,
  mergePlaces,
  placesFromTile,
  featureFromCoordinates,
} from './mapUtils'

import {
  getClusters,
  getFeaturesInBounds,
  getLocationFromPoint,
  getRadius
} from 'vibemap-constants/dist/map.js'
import { scorePlaces, getBoundary } from 'vibemap-constants/dist/helpers.js'

import Boundary from './boundary'
import Markers from './markers'
import MarkerImage from './marker/markerImage'
import Pin from './marker/pin'

import mapStyles from './map_styles.js'
import Icon from 'components/elements/icons/iconCategory'
import "./map.scss"

const Map = ({
  children,
  city,
  cooperativeGestures = true,
  dragPan = true,
  fitPlacesToBounds = true,
  fitMapToBoundaries = true,
  fitMarkers = false,
  hasPlace,
  heatmap,
  height,
  width,
  link,
  boundsToMarkers,
  handleMarkerClick,
  map3d,
  mapOptions,
  markerStyle,
  minZoom = 0,
  offsetFilterHeight,
  openInApp,
  scrollZoom,
  showBoundary,
  showAsMobile,
  showCards,
  showCircles,
  showMarkers,
  showMarkerLabel,
  showFullScreen,
  showExplore,
  showEvents,
  showHeat,
  showPhotoMarkers,
  showPlaces,
  showTextOnly,
  showTransit,
  placesLayer,
  loadFromTile,
  routeLayer,
  mapTheme,
  ...props
} = {}) => {

  //shouldDebug && useTilg()

  const filters = filterStore((state) => state.filters)
  const tagsCurrent = filterStore((state) => state.tagsCurrent)
  const hasActivitiesFilter = filterStore((state) => state.hasActivitiesFilter)
  const hasEditorialCategoryFilter = filterStore((state) => state.hasEditorialCategoryFilter)
  const hasSearchFilter = filterStore((state) => state.hasSearchFilter)
  const hasTagsFilter = filterStore((state) => state.hasTagsFilter)
  const hasVibesFilter = filterStore((state) => state.hasVibesFilter)
  const searchRadius = filterStore((state) => state.searchRadius)
  const setSearchRadius = filterStore((state) => state.setSearchRadius)
  const shouldClipToViewport = !hasEditorialCategoryFilter // FIXME: bring this together with clipTOBounds in Zustand
  const shouldMergeAndSort = !hasEditorialCategoryFilter

  const isClient = pageStore((state) => state.isClient)
  const isMobile = pageStore((state) => state.isMobile)
  const placeLayout = pageStore((state) => state.placeLayout)
  const setPageHasMap = pageStore((state) => state.setPageHasMap)

  const themeContext = useContextTheme()
  const { theme, themeClass } = themeContext

  const {
    categories_top_slugs,
    events,
    numEvents,
    places,
    placeCurrent,
    placeCurrentGEOJSON,
    placeFocused,
    routeData,
    setPlacesLoaded,
    setPlaceCurrent,
    setEvents,
    setPlacesInBounds,
    setRouteData,
  } = dataStore((state) => state);

  const cityCurrent = locationStore((state) => state.cityCurrent)
  const currentLocation = locationStore((state) => state.locationCurrent)
  const hasLocation = locationStore((state) => state.hasLocation)
  const userLocation = locationStore((state) => state.locationUser)
  const popup = locationStore((state) => state.popup)

  const placeGeometry = placeCurrent?.geometry
  const placeLocation = placeGeometry && locationFromGeometry(placeGeometry)

  const currentLatitude = placeLocation
    ? placeLocation?.latitude
    : currentLocation?.latitude

  const currentLongitude = placeLocation
    ? placeLocation?.longitude
    : currentLocation?.longitude

  const setCurrentLocation = locationStore((state) => state.setLocationCurrent)
  const zoomCurrent = locationStore((state) => state.zoomCurrent)
  const zoomPrevious = locationStore((state) => state.zoomPrevious)
  const setZoomCurrent = locationStore((state) => state.setZoomCurrent)
  const setZoomPrevious = locationStore((state) => state.setZoomPrevious)

  const {
    //boundaries,
    clipToBounds,
    hasTouchedMap,
    mapLocationCurrent: currentMapLocation,
    mapPaddingBounds,
    mapUpdated,
    numImageMarkers,
    numPlacesToStore,
    mapUpdateByUser: userChangedMap,
    showMarkerImage, shouldCluster, shouldNumber,
    showRoute, showMapCards,
    shouldFetchEvents,
    setBoundaries,
    setHasTouchedMap,
    setMapLocationCurrent,
    setMapReady,
    setMapUpdated,
    setMapUpdateByUser: setUserChangedMap,
  } = mapStore((state) => state);

  const setBoundary = showBoundary || clipToBounds
  const { boundaries, geoJSON, error } = useFetchBoundary(showBoundary, cityCurrent, setBoundaries);
  const showHeatGlobal = showHeat && !hasEditorialCategoryFilter && !hasTagsFilter


  const [exploreLink, setExploreLink] = useState('map')

  const hasZoomChanged = hasPlace
    ? props.zoom != defaultValues.zoomDetails
    : props.zoom != defaultValues.zoom

  const newZoom = zoomCurrent && !hasZoomChanged
    ? zoomCurrent
    : props.zoom

  // Mapbox React GL settings
  // TODO: Make these configurable from Wordpress
  const settings = useMemo(() => ({
    dragPan: !isMobile,
    dragRotate: !isMobile,
    scrollZoom: false, // was !isMobile,
    touchActions: cooperativeGestures ? 'pan-y pinch-zoom' : 'pan-y',
    touchZoom: true,
    touchRotate: true,
    keyboard: true,
    doubleClickZoom: true,
    minZoom: minZoom ? minZoom : 0,
    maxZoom: 20,
    minPitch: 0,
    maxPitch: 85
  }), [isMobile, minZoom]);

  // Set the map object by ref for entired component
  const mapRef = props.mapRef ? props.mapRef : useRef(null)
  const getMap = () => {
    const map = mapRef?.current
      ? mapRef.current.getMap()
      : null
    return map
  }
  const map = getMap()

  // Viewport is controled by the react-map-gl compnoent
  const latitude = props?.latitude ? props.latitude : currentLatitude
  const longitude = props?.longitude ? props.longitude : currentLongitude

  const [viewport, setViewport] = useReducer(reducerDeepCompare, {
    width: props.width,
    height: props.height,
    bearing: props.bearing,
    pitch: props.pitch,
    latitude: latitude,
    longitude: longitude,
    showPopup: true,
    zoom: newZoom
  })

  /*
  useWhatChanged([
    viewport,
    zoomCurrent,
    currentLocation,
    boundaries
  ]);
  */

  // Handle zoom change from parent
  useEffect(() => {
    const newZoom = zoomCurrent && !hasZoomChanged
      ? zoomCurrent
      : props.zoom

    // Has changed?
    const zoomChanged = newZoom != viewport.zoom
    //console.log('DEBUG: zoomCurrent, zoomPrevious ', zoomCurrent, zoomPrevious, zoomChanged);

    if (zoomChanged) {
      const new_viewport = { ...viewport, zoom: newZoom }
      //setViewport(new_viewport)
    }

  }, [props.zoom, zoomCurrent])


  const filtersDep = JSON.stringify(filters)
  const tagsCurrentDep = JSON.stringify(tagsCurrent);
  const [heatmapStyle, setHeatmapStyle] = useState(mapStyles.heatmap)
  const [circleStyle, setCircleStyle] = useState(mapStyles.circles)

  useEffect(() => {
    console.log('DEBUG: filters changed ', filtersDep);
    // For either current category or tag, see if there's a theme color
    const categoryColors = theme?.map?.categoryColors
    const tag = tagsCurrent && tagsCurrent.length > 0 && tagsCurrent[0].toLowerCase()
    const themeColor = categoryColors && categoryColors.hasOwnProperty(tag) && categoryColors[tag]
    const heatmapStyle = themeColor && mapStyles[`heatmap_${themeColor}`]
      ? mapStyles[`heatmap_${themeColor}`]
      : mapStyles.heatmap

    const circleStyle = themeColor && mapStyles[`circles_${themeColor}`]
      ? mapStyles[`circles_${themeColor}`]
      : mapStyles.circles

    setHeatmapStyle(heatmapStyle)
    setCircleStyle(circleStyle)
  }, [filtersDep, tagsCurrentDep])


  const [bounds, setBounds] = useReducer(reducerDeepCompare, null)
  const [center, setCenter] = useState([0, 0])
  const [clusterSize, setClusterSize] = useState(defaultValues.clusterSize) // Default cluster size, divided by 1000 meters?

  // Effect for viewport update on location
  // TODO: Handle user selected location, city location, and default predicted location.
  const currentLocationDep = JSON.stringify(currentLocation)
  const placeCurrentDep = JSON.stringify(placeCurrent)

  useEffect(() => {
    const { city, bearing } = props
    const hasCity = city && city.length > 0
    console.log('DEBUG: currentLocationDep ', currentLocationDep);

    if (hasLocation && (userLocation || cityCurrent || placeCurrent || hasCity)) {
      // See algorithm in Template Context
      const locationFromPlace = placeCurrent && getLocationFromPoint(placeCurrent?.geometry?.coordinates)
      const location = locationFromPlace && !userChangedMap
        ? locationFromPlace
        : currentLocation

      // If location changes, but not because user moved the map
      if (location && !userChangedMap) {
        const latitude = props?.latitude ? props.latitude : location?.latitude
        const longitude = props?.longitude ? props.longitude : location?.longitude

        let new_viewport = viewport
        const new_zoom = zoomCurrent && !hasZoomChanged
          ? zoomCurrent
          : location?.zoom
            ? parseInt(location.zoom)
            : props.zoom

        new_viewport.bearing = bearing
        new_viewport.latitude = latitude
        new_viewport.longitude = longitude
        new_viewport.zoom = new_zoom

        const viewport_changed = !isEqual(viewport, new_viewport)
        if (viewport_changed) {
          console.log('DEBUG: new_viewport ', new_viewport)
          setViewport(new_viewport)
        }

        const location_changed = !isEqual(currentLocation, location)
        if (location_changed) {
          setCurrentLocation(location)
        }

        // FIXME: setcurrent location, if the map has moved
      }
    }
  }, [
    currentLocationDep,
    placeCurrentDep,
    currentLongitude,
    currentLatitude,
    mapUpdated
  ])


  // Set global context that there's a map
  useEffect(() => {
    setPageHasMap(true)
  }, [])


  // FIXME: handle place to GeoJSON oustide of map?
  const [eventsGeoJSON, setEventsGeoJSON] = useReducer(reducerDeepCompare, {
    type: "FeatureCollection",
    features: [],
  })


  const [placesGeoJSON, setPlacesGeoJSON] = useReducer(reducerDeepCompare, {
    type: "FeatureCollection",
    features: [],
  })

  const [eventMarkers, setEventMarkers] = useReducer(reducerDeepCompare, [])
  const [placeMarkers, setPlaceMarkers] = useReducer(reducerDeepCompare, [])
  const [shouldUpdateMarkers, setShouldUpdateMarkers] = useState(true)
  const [currentMarker, setCurrentMarker] = useReducer(reducerDeepCompare, null)
  const [marker, setMarker] = useReducer(reducerDeepCompare, {
    latitude: props.latitude,
    longitude: props.longitude
  })

  const shouldEventMarkers = showEvents && eventMarkers?.length > 0 && markerStyle == 'images'


  // Get thumbnail for feature marker
  // TODO: Make this a reusable util
  const getThumbnails = (feature) => {
    const { images, vibes, vibemap_images = [], thumbnail_url } = feature.properties

    const thumbs = []

    const best_thumbnail = thumbnail_url
      ? thumbnail_url
      : vibemap_images && vibemap_images.length > 0
        ? typeof (vibemap_images[0]) == 'string'
          ? vibemap_images[0]
          : vibemap_images[0]?.thumbnail_url
            ? vibemap_images[0]?.thumbnail_url
            : null
        : null

    if (best_thumbnail) thumbs.push(best_thumbnail)

    if (feature.properties?.hotspots_place) {
      const best_places_thumb = getThumbnails(feature.properties?.hotspots_place)
      if (best_places_thumb) thumbs.push(best_places_thumb)
    }

    return thumbs?.length > 0
      ? thumbs[0]
      : null
  }

  // 🪄 Popup state
  const popUpDep = JSON.stringify(popup)
  //const [mapOffset, setMapOffset] = useState({ left: 0, right: 0, top: 0, bottom: 0 })
  const [centerOriginal, setCenterOriginal] = useState(null)
  const [popupInfo, setPopupInfo] = useState(null)
  useEffect(() => {
    if (popup) {
      setPopupInfo(popup)
    }
  }, [popUpDep])


  // Set popup or featured marker from featuredMarker in theme
  const featuredMarker = theme?.map?.markerFeatured
  useEffect(() => {
    if (featuredMarker && isMobile == false) {
      const { latitude, longitude, name, image, style } = featuredMarker
      /* const new_location = {
        ...currentLocation,
        latitude: latitude,
        longitude: longitude,
        fromUser: true,
        userRequested: false,
        centerpoint: [longitude, latitude]
      }
      setCurrentLocation(new_location) */

      const popUpInfo = {
        close: true,
        image: image,
        name: name,
        latitude: latitude,
        longitude: longitude,
      }

      setPopupInfo(popUpInfo)
    }
  }, [featuredMarker])


  // Add location and zoom to link
  let query = {}

  // 🪄 TODO: isolate map and location side efects
  useDeepCompareEffect(() => {
    (async () => {
      //console.log('Inside update map effect ', mapUpdated);
      updateMap()

      // Update link
      const params = new URLSearchParams(query)
      const newLink = link + params.toString()
      if (exploreLink != newLink) setExploreLink()

      if (showRoute && places && places.length > 0) {
        const route = await getRoute(places)
        console.log('Route data ', route);
        setRouteData(route)
      }
    })()

  }, [placesGeoJSON, mapUpdated])

  // 🪄 Sync Markers with Top Places
  const alreadyFitMarkers = useRef(false)
  const prevPlacesInBoundsRef = useRef([]);


  // 🪄 Update Map bounds based on data
  const matchBoundsToData = (places) => {
    if (showPlaces && places && places.length > 0) {
      // For each place add it's index as a property
      // TODO: make this a util or hook; and earlier in process
      let geojson = featureCollection(places.map((place, index) => {
        const num = index + 1
        place.properties.index = num
        return place
      }))
      setPlacesGeoJSON(geojson)
      const bounds = bbox(geojson)

      // Fit markers on the first render
      const shouldFitMarkers = map && bounds && fitMarkers && !alreadyFitMarkers.current
      if (shouldFitMarkers) {
        try {
          map.fitBounds(bounds, {
            padding: mapPaddingBounds,
            easing: function () { return 1 }
          })

          // Restore bearing and pitch
          map.once('moveend', () => {
            map.setBearing(props.bearing);
            map.setPitch(props.pitch);
          })

          alreadyFitMarkers.current = true
        } catch (error) {
          console.log('Error: problem with bounds ', error, bounds, geojson);
        }
      }
    }
  }

  // Places to Markers
  const showForPrint = placeLayout == 'print'
  const showNumbers = showForPrint || shouldNumber ? true : false

  useDeepCompareEffect(() => {
    // Set GeoJSON for places and handle marker bounds
    matchBoundsToData(places)

    const hasMapAndBounds = bounds || map
    //console.log('showMarkers && updateMarkers && bounds ', showMarkers, updateMarkers, bounds, map);
    if (showMarkers && shouldUpdateMarkers && hasMapAndBounds) {
      // Update places to only fit map viewport/bounds
      const placesInBounds = shouldClipToViewport && hasPlace == false
        ? featuresToBounds(map, places, fitPlacesToBounds)
        : places

      //console.log('placesInBounds ', hasPlace, placesInBounds.length, placesInBounds);

      // TODO: Make this a util and handle in global state
      const allow_subset = true
      const placesHaveChanged = !compareLists(prevPlacesInBoundsRef.current, placesInBounds)
      const numMarkers = showNumbers ? placesInBounds.length : numImageMarkers

      if (placesHaveChanged || showNumbers) {
        setPlacesInBounds(placesInBounds)
        prevPlacesInBoundsRef.current = placesInBounds

        // Get top marker clusters
        const topClusters = getTopMarkerClusters({
          places: placesInBounds,
          numMarkers: numMarkers,
          shouldCluster: shouldCluster,
          clusterSize: clusterSize
        })


        const topWithTopCategory = topClusters.map((place, index) => {
          const properties = place?.properties ?? {};
          const { categories } = properties

          // FIXME: best if this comes from the API
          const topLevelCats = categories?.filter(cat => categories_top_slugs?.includes(cat))
          const firstCategory = topLevelCats && topLevelCats.length > 0
            ? topLevelCats[0]
            : null

          place.properties ??= {};
          place.properties.top_category = firstCategory;
          return place
        })

        const markersTopNew = placeCurrentGEOJSON && !showRoute
          ? [placeCurrentGEOJSON, ...topWithTopCategory]
          : topWithTopCategory

        setPlaceMarkers(markersTopNew)
      }
    }

  }, [places, clusterSize, zoomCurrent, map, bounds, showNumbers])

  // TODO: Does this need to be set each time?
  const has_places_data = placesLayer.length > 0
  const has_route_data = routeData && routeData?.geometry?.coordinates.length > 0
  const routeColor = theme?.accent
  const routeStyle = {
    ...mapStyles.route_paint,
    'line-color': routeColor
  }

  const routeArrowStyle = {
    ...mapStyles.route_arrow_paint,
    'text-color': routeColor
  }


  const emptySource = { type: 'FeatureCollection', features: [] }
  let places_layer_geojson = null
  if (has_places_data) places_layer_geojson = featureCollection(placesLayer)

  // 🪄 Fix map to boundaries, when first loaded
  const boundariesDep = useMemo(() => boundaries, [boundaries])
  const [firstLoad, setFirstLoad] = useState(true)
  // So this should only be used when the map is first loaded, not after user interaction.
  useEffect(() => {
    const hasBoundaries = boundaries && boundaries?.type && boundaries?.coordinates
    const shouldFit = map && firstLoad && hasBoundaries && fitMapToBoundaries

    if (shouldFit) {
      const feature = featureFromCoordinates(boundaries)
      const bounds = bbox(feature)
      const paddingAmount = isMobile ? 20 : 40
      try {
        map.fitBounds(bounds, { padding: paddingAmount })
      } catch (error) {
        console.log('Error: problem with bounds ', error, bounds, feature);
      }

      map.once('moveend', function () {
        if (props.bearing && props.bearing !== 0) {
          map.setBearing(props.bearing);
        }
      })
      //if (props.bearing && props.bearing !== 0) map.setBearing(props.bearing)
      setFirstLoad(false)
    }

    setMapReady(true)

  }, [map, boundariesDep])

  // 🪄 Sync Events Markers
  // If bounds or zoom change
  useDeepCompareEffect(() => {
    if (showEvents) {
      let geojson = featureCollection(events)
      setEventsGeoJSON(geojson)
    }

    const hasMapOrBounds = (bounds || map)
    //console.log('showMarkers && updateMarkers && bounds ', showMarkers, updateMarkers, bounds, map);
    if (showMarkers && shouldUpdateMarkers && hasMapOrBounds) {
      const initialBounds = map.getBounds().toArray()

      const eventsInBounds = fitPlacesToBounds
        ? getFeaturesInBounds(events, initialBounds)
        : events

      setEvents(eventsInBounds)

      const eventsTop = eventsInBounds.slice(0, numEvents < numImageMarkers ? numImageMarkers : numEvents) // Only show top
      const eventsClustered = clusterSize && !isNaN(clusterSize)
        ? getClusters(eventsTop, clusterSize)
        : eventsTop
      setEventMarkers([...eventsClustered])
    }
  }, [events, zoomCurrent, bounds])

  // 🪄 Sets marker location from current place
  const currentMapLocationDep = JSON.stringify(currentMapLocation)
  useEffect(() => {
    const mapLocation = currentMapLocation
      ? currentMapLocation
      : placeCurrent

    if (mapLocation) {
      const feature = mapLocation

      const {
        categories,
        slug,
        vibemap_images = [],
        thumbnail_url
      } = feature

      const firstCategory = categories?.length > 0 && categories[0]
      const showPhoto = true
      const showPulse = true
      const thumbnail = thumbnail_url
        ? thumbnail_url
        : vibemap_images && vibemap_images.length > 0
          ? vibemap_images[0].thumbnail_url
          : null

      const categoryIcon = <Icon category={firstCategory ? firstCategory : 'all'} />

      const imageSize = 60
      const image = showPhoto
        ? <img
            loading='lazy'
            height={`${imageSize}px`}
            width={`${imageSize}px`}
            src={thumbnail} />
        : categoryIcon

      const longitude = feature.geometry ? feature.geometry.coordinates[0] : 0
      const latitude = feature.geometry ? feature.geometry.coordinates[1] : 0

      /*
      const marker = (
        <Marker
          key={slug}
          longitude={longitude}
          latitude={latitude}>
          {showPhoto && image && (
            <a
              onClick={((e) => handleClick(e, feature))}
              onMouseOver={((e) => handleOnMouseOver(e, feature))}
              onMouseOut={((e) => handleOnMouseOut(e, feature))}
              className={`marker ${showPhoto ? 'photo' : 'icon'}`}>
              {image}
            </a>
          )}
          {showPulse &&
            <Pulse show={showPulse} />
          }
        </Marker>
      )
      */
      const marker = <MarkerImage
        key={slug}
        longitude={longitude}
        latitude={latitude}
        image={image}
      />
      setCurrentMarker(marker)

      // And then also move the map to that marker
      if (feature && feature.geometry) {
        const new_latitude = feature.geometry.coordinates[1]
        const new_longitude = feature.geometry.coordinates[0]

        if (new_latitude !== viewport.latitude || new_longitude !== viewport.longitude) {
          // Consider doing the same for pitch
          const diff_latitude = centerOriginal ? (centerOriginal.lat - new_latitude) * 100 : 0

          let new_viewport = {
            ...viewport,
            latitude: new_latitude,
            longitude: new_longitude
          }
          //new_viewport.zoom = mapStyles.MAPBOX_ZOOM_DETAILS

          if (map3d) {
            // TODO: add style bearing shift
            // new_viewport.bearing = diff_latitude
          }
          //console.log('DEBUG: new_viewport currentMapLocationDep ', new_viewport)
          setViewport(new_viewport)
        }
      }
    }

    if (placeCurrent) {
      // Add to place markers
      const shouldPrepend = !showRoute && placeMarkers.some(marker => isEqual(marker, placeCurrentGEOJSON));
      shouldPrepend && setPlaceMarkers([placeCurrentGEOJSON, ...placeMarkers])
    }
  }, [
    placeCurrentDep,
    currentMapLocationDep // FIXME: currentMapLocation is causing issues
  ])


  // Handle Map Events
  const onViewportChange = (viewport_event, event) => {

    const new_viewport = viewport_event?.viewState
      ? viewport_event.viewState
      : viewport_event

    // Handle user interactions
    if (event?.isDragging) {
      handleDragEnd(event)
    }

    if (event?.isPanning || event?.isZooming) {
      handleZoomEnd(event)
    }

    // Keep internal map state
    const viewport_changed = !isEqual(viewport, new_viewport)

    // Don't update/move map on transitions
    const places_loaded = placesGeoJSON?.features?.length > 0
    if (viewport_changed) {
      setViewport(new_viewport)
      debounceLocation(new_viewport, false)

      // And call callback, if provided
      if ("onViewportChange" in props) props.onViewportChange(new_viewport)
    }
  }

  const debounceLocation = useDebouncedCallback((viewport, set_bounds = true) => {
    const new_latitude = viewport?.latitude
    const new_longitude = viewport?.longitude

    const new_location = {
      ...currentLocation,
      latitude: new_latitude,
      longitude: new_longitude,
      fromUser: true,
      userRequested: false,
      centerpoint: [new_longitude, new_latitude]
    }

    const location_changed = !isEqual(currentLocation, new_location)
    if (location_changed && map) {
      const newBounds = map.getBounds().toArray()
      if (set_bounds && newBounds?.length) setBounds(newBounds)

      // FIXME: This competes with location from template
      // Only set if the user has interacted
      //setMapLocationCurrent(new_location)
      const location_diff = Math.abs(new_latitude - currentLatitude) + Math.abs(new_longitude - currentLongitude)
      const zoom_diff = Math.abs(viewport.zoom - zoomCurrent)
      const user_changed_map = location_diff > 0.005 || zoom_diff > 0.005
      //console.log('DEBUG ', location_diff, zoom_diff, user_changed_map);
      if (user_changed_map) {
        setZoomCurrent(viewport.zoom)
        setCurrentLocation(new_location)
      }
    }

  }, 500 // delay in ms
  )

  // Handle events
  const onChange = (event) => {
    // Marker on change
    console.log('onChange ', event)
    setMarker({ longitude: event.lngLat[0], latitude: event.lngLat[1] });
    props.onChange(event)
  }

  const [cursor, setCursor] = useState('auto')
  const _onHover = (event) => {
    // Show tooltips on layer hover.
    const feature = event.features && event.features[0];
    const featureLocation = feature?.properties?.location && JSON.parse(feature?.properties?.location)

    // There a layer & feature
    if (feature && event.features.length > 0) {
      let first_feature = event.features[0]

      const is_marker = first_feature.layer.id === "markers" || first_feature.layer.id === "circles"
      const new_cursor = is_marker ? 'pointer' : 'auto'
      if (new_cursor != cursor) setCursor(new_cursor)

      if (first_feature.properties.name && is_marker) {
        //console.log('Hovered on feature: ', first_feature.properties.name, event.lngLat[1], event.lngLat[0])
        const latitude = featureLocation
          ? featureLocation?.lat
          : event?.lngLat?.lat
        const longitude = featureLocation
          ? featureLocation?.lon
          : event?.lngLat?.lng

        // Hanlde any errors with incorrect coords
        try {
          showPopup(first_feature.properties.name, latitude, longitude, feature)
        } catch (error) {
          console.log('ERROR: problem with popup ', error);
        }
      }
    }
  }

  const _onBlur = (event) => {
    // Hide tooltips on blur.
    setPopupInfo(null)
    setCursor('auto')
    _onHover(event)
  }

  // Marker hover
  const handleOnMouseOver = (_event, feature) => {
    const longitude = feature.geometry.coordinates[0]
    const latitude = feature.geometry.coordinates[1]
    showPopup(feature.properties.name, latitude, longitude, feature)
  }

  const handleOnMouseOut = () => {
    //setPopupInfo(null)
  }

  const handleDragEnd = useDebouncedCallback((event) => {
    if (map) {
      const center = map.getCenter()
      const zoom = map.getZoom()
      const zoomNew = event?.viewState?.zoom
      setUserChangedMap(true)
      //debounceLocation(center)
      const new_location = {
        ...currentLocation,
        latitude: center.lat,
        longitude: center.lng,
        fromUser: true,
        userRequested: false,
        centerpoint: [center.lng, center.lat]
      }
      setCurrentLocation(new_location)
      setZoomCurrent(zoom)
      //console.log('handleDragEnd ', zoom, new_location);
    }
  })

  const handleZoomEnd = useDebouncedCallback((event) => {
    if (map) {
      const newZoom = event?.viewState?.zoom || map.getZoom()
      const newBounds = map.getBounds().toArray()
      //console.log('DEBUG handleZoomEnd ', event, newZoom);

      setUserChangedMap(true)
      setViewport(event?.viewState)
      setZoomCurrent(newZoom)
      newBounds?.length && setBounds(newBounds)
    }
  })

  // Sync cluster and heatmap values
  // `clusterSizeBase`: The base size of the cluster.
  // `zoomStart`: The initial zoom level when the calculation started.
  // `viewport.zoom`: The current zoom level of the viewport.
  // `clusterMultiply`: A factor used to adjust the rate at which the cluster size changes with the zoom level.
  const handleClusters = useDebouncedCallback((event) => {
    const zoomStart = mapStyles.MAPBOX_ZOOM_DEFAULT - 2 // Start at 2 zoom levels higher
    // Smaller clusterSize and therefore more markers, when zoomed in
    const clusterSizeBase = defaultValues.clusterSize
    const clusterMultiply = 4 // Higher number means more clusters? 2-8 seems to work well
    const zoomRatio = viewport.zoom / zoomStart
    const newClusterSize = (clusterSizeBase * zoomStart) / (Math.pow(zoomRatio, clusterMultiply) * viewport.zoom)

    setClusterSize(newClusterSize)
  })

  // Recalc clusters on zoom
  useEffect(() => {
    handleClusters()
  }, [zoomCurrent])


  // Handle search radius
  const handleSearchRadius = useDebouncedCallback((radius) => {
    // Only update if the radius has changed significanly
    if (Math.abs(radius - searchRadius) >= 0.5) {
      setSearchRadius(radius);
    }
  }, 500);


  const handleMapUpdate = () => {
    const map = getMap()

    if (!map) return false


    map.resize() // Triggert tile load
    //console.log('DEBUG: Map loaded handleMapUpdate !!! ', map)

    const boundsNew = map.getBounds().toArray();
    if (boundsNew.length && !isEqual(bounds, boundsNew)) {
      setBounds(boundsNew);
    }

    const boundsFlat = flatBoundsFromBounds(boundsNew)
    const newSearchRadius = getRadius(boundsFlat)
    handleSearchRadius(newSearchRadius)

    const refCenter = map.getCenter()
    const zoom = map.getZoom()

    setCenter(refCenter)
    if (!centerOriginal) setCenterOriginal(refCenter)
  }


  const justClickedRef = useRef(false)
  const handleClick = useCallback((_event, feature) => {
    console.log('DEBUG handleMarkerClick / handleClick in map/index ', _event, feature)
    _event.preventDefault();
    _event.stopPropagation();

    if (justClickedRef.current != true && feature) {
      // Handle nested string from tile
      const featureDecoded = decodeFeature(feature)
      // Update the cards index for the selected feature
      // FIXME: if features already selected, go to it
      // ALREADY called? setPlaceCurrent(featureDecoded)
      // ALREADY called? setZoomPrevious(zoomCurrent)
      if (handleMarkerClick) handleMarkerClick(featureDecoded)

      justClickedRef.current = true
      setTimeout(() => {
        justClickedRef.current = false
      }, 200);
    }
    // TODO: Make adjustments if cards are displayed
  }, []);


  const handleLayerClick = (event) => {

    // If user first taps on mobile, enable more features
    setHasTouchedMap(true)

    const feature = event?.features && event.features[0]
    console.log('handleClick ', event, feature)

    if (feature) {
      const featureDecoded = decodeFeature(feature)
      setPlaceCurrent(featureDecoded)
      setZoomPrevious(zoomCurrent)
      if (handleMarkerClick) handleMarkerClick(feature)
    }
    // TODO: Make adjustments if cards are displayed
  }


  const mapLoaded = () => {
    // Set map reference once for components, when it ready
    handleMapUpdate()

    // Then handle tile sources
    handleSource(map, null, true)

    // FIXME: These are not working; need to write a custom controller
    map.scrollZoom.setWheelZoomRate(1 / 2000); // Default is 1/450

    // Slow the pan speed
    map.dragPan.enable({
      linearity: 0.3,
      easing: t => t,
      maxSpeed: 10,
      deceleration: 2500
    });

    try {
      // Tile layer events
      map.on('data', handleSource)
      map.on('sourcedata', handleSource)

      const tilesLoaded = map.areTilesLoaded()

      map.on('render', (e) => {
        //console.log('DEBUG: Map renedered')
      })

      map.on('error', (error) => {
        console.log('ERROR: Map error ', error);
      })

      map.on('zoomend', (event) => {
        handleZoomEnd(event)
      })

    } catch (error) {
      console.log('Error with map ', error)
    }
  }


  // Called with map first loads
  const updateMap = () => {
    const { locationData } = props

    handleMapUpdate()

    const map = getMap()
    if (map) {

      /* FIXME: this is  out-of-place
      query.latitude = center.lat,
        query.longitude = center.lng,
        query.zoom = zoom
      */

      if (setMapUpdated) setMapUpdated(true)

    }

    // If the place list changes
    let newGeoJSON = featureCollection(places)

    // TODO: Make bounds to marker it own event/step
    if (boundsToMarkers && showPlaces && placesGeoJSON.features.length > 1 && !locationData) {
      var bounds = bbox(placesGeoJSON)

      try {
        const boundsViewport = new WebMercatorViewport(new_viewport)
        const newBounds = [
          [bounds[0], bounds[1]], // Southwest
          [bounds[2], bounds[3]], // Northwest
        ]

        const { longitude, latitude, zoom } = boundsViewport.fitBounds(newBounds, { padding: 40 })

        const new_viewport = {
          ...viewport,
          longitude,
          latitude,
          zoom,
        }
        setViewport(new_viewport)

      } catch (error) {
        console.log("Error fitting bounds: ", error)
      }
    }

    if (placesGeoJSON.features.length !== newGeoJSON.features.length) {
      setPlacesGeoJSON({ ...newGeoJSON })
    }

    // TODO: Make this a useEffect for updating markers
    /*
    let shouldUpdateMarkers = false
    if (placeCurrent) {
      if (prevPlace.current && prevPlace.current.id !== placeCurrent.id) {
        shouldUpdateMarkers = true
      }
      prevPlace.current = placeCurrent
    }
    console.log('places vs markers ', );
    if (places.length !== markers.length) shouldUpdateMarkers = true
    setUpdateMarkers(shouldUpdateMarkers)
    */
  }


  const handleSource = (e, loaded) => {
    // Is the source and tile loaded
    const isSourceLoaded = e ? e.isSourceLoaded : false
    const sourceId = e ? e.sourceId : null

    const map = getMap()
    if (map) {
      const initialBounds = map.getBounds().toArray()
      initialBounds?.length && setBounds(initialBounds)
      const boundsArrayFlat = initialBounds[0].concat(initialBounds[1])

      const newSearchRadius = getRadius(boundsArrayFlat)
      // FIXME: this trigger a bunch of changes to the search radius
      // And that causes dependency problems downstream
      handleSearchRadius(newSearchRadius)

      // Map is loaded or places layer changed
      const loadedLayer = (
        isSourceLoaded && sourceId === 'public.places_vt' ||
        isSourceLoaded && sourceId === 'places_data' ||
        isSourceLoaded && sourceId === 'composite'
      )
      if (loaded || loadedLayer) {
        // Loads date to json that is filtered by the client based on nav state
        const source_features = map.querySourceFeatures('places', { sourceLayer: 'heatmap' })
        const centerpoint = [viewport.longitude, viewport.latitude]

        const places_from_tile = placesFromTile(source_features, 'places', initialBounds, centerpoint, viewport.zoom)

        const places_sorted_from_tile = scorePlaces(places_from_tile, [viewport.longitude, viewport.latitude], filters.vibes, undefined, 'vibe')
        //console.log('places_from_tile ', places_from_tile, source_features);
        const placesMerged = mergePlaces(places, places_sorted_from_tile)

        const placesInBounds = fitPlacesToBounds && shouldClipToViewport
          ? getFeaturesInBounds(placesMerged, initialBounds)
          : placesMerged

        // If there's a search filter, then don't merge and sort places
        const placesScored = shouldMergeAndSort
          ? placesInBounds.sort((a, b) => b.properties.vibe_score - a.properties.vibe_score)
          : placesInBounds

        const placesClustered = clusterSize && !isNaN(clusterSize)
          ? getClusters(placesScored, clusterSize)
          : placesScored

        // Only set places from tiles is there's not search/filter
        const hasSearchFilters = hasActivitiesFilter || hasTagsFilter || hasVibesFilter || hasSearchFilter
        const placesReduced = placesClustered.slice(0, numPlacesToStore)

        if (!hasSearchFilters && placesClustered.length > 0) {
          // FIXME: This is not the best way to merge tile layer data
          //setPlaces([...placesReduced])
          setPlacesLoaded(true)
        }

        // Hide the transit_routes_simplified source layer if showTransit is false
        if (!showTransit) {
          try {
            //map.setLayoutProperty('transit_routes_simplified', 'visibility', 'none')
            map.setLayoutProperty('transit-stop-labels-mapbox', 'visibility', 'none')
            map.setLayoutProperty('transit-explorer-lines', 'visibility', 'none')
            map.setLayoutProperty('transit_low_zoom', 'visibility', 'none')
            map.setLayoutProperty('transit_routes', 'visibility', 'none')
          } catch (error) {
            console.log('Error hiding transit layers ', error)
          }

        } else {
          //map.setLayoutProperty('transit_routes_simplified', 'visibility', 'visible')
        }
      }
    }
  }


  const handleUserMapUpdate = (event) => {
    console.log('handleUserMapUpdate event ', viewport);
    if (mapRef.current) {

      if (viewport.zoom != zoomCurrent) {
        console.log('Zoom changed? ', zoomCurrent, zoom)
        console.log('TOOD: Update userChangedMap, if map changed ', userChangedMap)
      }
    }
  }


  const showPopup = (name, latitude, longitude, feature) => {
    const popUpInfo = {
      name: name,
      latitude: latitude,
      longitude: longitude,
      isMarkerImage: feature?.properties?.showImage,
    }
    setPopupInfo(popUpInfo)
  }

  const setRadiusAndBounds = (bounds) => {
    const flatBounds = flatBoundsFromBounds(bounds)
  }


  // Map styles & layout
  // TODO: set in state?
  let themeStyle = mapStyles.MAPBOX_STYLE
  if (mapTheme === 'gray') themeStyle = mapStyles.MAPBOX_STYLE_GRAY
  if (mapTheme === 'outdoors') themeStyle = mapStyles.MAPBOX_STYLE_OUTDOORS

  // Map layout logic
  const { offsetTop } = props
  const isOffset = offsetTop > 0

  const widthStyle = { maxWidth: '100%' }

  const pillHeight = 100
  const topMapPadding = 40
  const navButtonHeight = 114
  const navTop = isMobile ? pillHeight : topMapPadding

  const mapHeight = isOffset ? height + offsetTop : height
  const mapStyle = isOffset
    ? {
      ...widthStyle,
      marginTop: -offsetTop + 'px'
    }
    : { ...widthStyle }

  const touchAction = isMobile ? 'none' : 'pan-y'
  const mapContainerClass = `post-map-viewport${isOffset ? ' offset' : ''} ${showMapCards ? 'has-map-cards' : ''}`

  const controlLocation = 'bottom'
  const controlBottomMargin = '2.4rem'
  const controlRightMargin = '1.4rem'
  const navigationStyle = {
    bottom: controlLocation == 'bottom'
      ? isMobile ? `26rem` : controlBottomMargin
      : undefined,
    right: controlRightMargin,
    top: controlLocation == 'top'
      ? 10 + offsetFilterHeight // Complex calculation based on fitlers
      : undefined
  }

  const fullscreenStyle = {
    bottom: controlLocation == 'bottom'
      ? isMobile ? `26rem` : `16rem`
      : undefined,
    right: controlRightMargin,
    top: isMobile
      ? pillHeight + navButtonHeight + offsetTop
      : controlLocation == 'top'
        ? controlBottomMargin
        : undefined
  }

  const iconSize = getIconSizeFromZoom(viewport.zoom)
  const markerSize = isMobile ? 4.2 : 5.3
  const popUpStyle = {
    display: 'block',
    maxWidth: isMobile ? '70px' : '100px'
  }


  const markersOptions = {
    bounds,
    handleClick,
    handleOnMouseOver,
    handleOnMouseOut,
    iconSize,
    markerSizeRem: markerSize,
    numColor: theme?.accent,
    placeCurrent,
    placeFocused,
    shouldCluster,
    showNumbers,
    showMarkerImage
  }

  // Don't use SSR
  if(isClient != true) return null

  const layersInteractive = showMarkers ? ['markers', 'circles'] : ['circles']

  return (
    <div className={mapContainerClass}>
      <ReactMapGL
        {...viewport}
        {...settings}
        ref={mapRef}
        mapboxAccessToken={mapStyles.MAPBOX_TOKEN}
        mapStyle={themeStyle}
        className='react-map-gl'
        cooperativeGestures={cooperativeGestures} // TODO: set this at the right time
        cursor={cursor}
        mapOptions={mapOptions}
        dragPan={dragPan}
        touchAction={touchAction}
        interactiveLayerIds={layersInteractive}
        minZoom={minZoom}
        //onTouchStart={onMove}
        //onTouchMove={onMove}
        //
        //onViewportChange={onViewportChange}
        onClick={handleLayerClick}
        onHover={_onHover}
        onMouseOver={_onHover}
        onMouseEnter={_onHover}
        onMouseLeave={_onBlur}
        onLoad={mapLoaded}
        onMove={onViewportChange}
        //onMoveEnd={onViewportChange}
        onZoomEnd={handleZoomEnd}
        onData={handleSource}
        preserveDrawingBuffer={true} // IMPORTANT: for performance reason only do in print mode
        style={{
          width: '100%',
          height: mapHeight,
        }}
        //transition={{ duration: 200, delay: 0 }}
        //transitionDuration={200}
        //transitionInterpolator={new LinearInterpolator()}
        //scrollZoom={scrollZoom}
      >


        <NavigationControl
          className={`control nav`}
          position='bottom-right'
          showCompass={true}
          showZoom={!isMobile}
          style={navigationStyle}
          visualizePitch={true}
          />

        {showFullScreen &&
          <FullscreenControl className='control fullscreen' style={fullscreenStyle} />
        }

        {showBoundary && boundaries
          ? <Boundary boundaries={boundaries} geoJSON={geoJSON} />
          : null
        }

        {!isMobile
          ?  <div id="coordinates" style={{ position: 'absolute', bottom: '20px', right: '60px' }}>
                <div style={{ background: 'rgba(255, 255, 255, 0.2)', padding: '0.5rem', borderRadius: '0.5rem', fontSize: '0.8rem' }}>
                {viewport.latitude.toFixed(4)}, {viewport.longitude.toFixed(4)}, {viewport.zoom.toFixed(2)}
              </div>
            </div>
          : null
        }

        {props.showDraggableMarker &&
          <Marker
            longitude={marker.longitude}
            latitude={marker.latitude}
            offsetTop={-20}
            offsetLeft={-10}
            draggable
            //onDragStart={onMarkerDragStart}
            //onDrag={onMarkerDrag}
            onDragEnd={onChange}
          >
            <Pin height={40} width={32} />
          </Marker>
        }

        {shouldEventMarkers
          ? <Markers
              {...markersOptions}
              features={eventMarkers}
              isMobile={isMobile} />
          : null}

        {showMarkers && showPhotoMarkers && showPlaces && markerStyle == 'images'
          ? <Markers
              {...markersOptions}
              features={placeMarkers}
              isMobile={isMobile}
              placeCurrent={placeCurrent}
              />
          : null}

        {mapUpdated && showPlaces && (
          <Source
            id='markers'
            type="geojson"
            data={placesGeoJSON  ? placesGeoJSON : emptySource}>

            {showHeat
              ? <Layer
                  id='heatmap'
                  type='heatmap'
                  minzoom={minZoom}
                  maxzoom={20}
                  paint={heatmapStyle}
                  isLayerChecked={true} />
              : null
            }

            {/* IMPORANT: Must be shown, event if showMarkers is false */}
            <Layer
                  id='markers'
                  type="symbol"
                  layout={mapStyles.marker_layout}
                  paint={mapStyles.marker_paint}
                />

            {showCircles
              ? <Layer
                  id='places_dots'
                  key='places_dots'
                  type='circle'
                  beforeId='markers'
                  //beforeId='slow street icon'
                  //layout={mapStyles.marker_layout_icons}
                  paint={mapStyles.circles_light} />
              : null
            }

            {showCircles
              ? <Layer
                  id='circles'
                  type='circle'
                  minzoom={minZoom}
                  maxzoom={20}
                  paint={circleStyle}
                  isLayerChecked={true} />
              : null
            }

            {showCircles
              ? <Layer
                  id='numbers'
                  type='symbol'
                  minzoom={minZoom}
                  maxzoom={20}
                  layout={{
                    "text-allow-overlap": true,
                    "text-letter-spacing": -0.08,
                    "text-field": ["get", "index"],
                    "text-font": [
                      "Public Sans SemiBold",
                      "Arial Unicode MS Regular"
                    ],
                    "text-size": 10
                  }}
                  paint={{
                    "text-color": "#FFF",
                  }}
                  isLayerChecked={true} />
              : null
            }

            {showTextOnly
              ? <Layer
                  id='places_markers'
                  key='places_markers'
                  //beforeId='photo_markers'
                  minzoom={minZoom}
                  maxzoom={20}
                  type='symbol'
                  //beforeId='slow street icon'
                  layout={mapStyles.marker_layout_text}
                  paint={mapStyles.marker_paint} />
              : null
            }


          </Source>
        )}
        {(showHeatGlobal && loadFromTile && (heatmap || showPlaces)) &&
          <Source
            id='heatmap'
            type="vector"
            url="https://api.vibemap.com/tile_json"
            cluster={false}>

            {/* IMPORTANT: The layer name/id must match the JSON response, i.e. */}
            <Layer
              id='heatmap'
              type='heatmap'
              minzoom={minZoom}
              maxzoom={20}
              source-layer='heatmap'
              beforeId='road-label'
              paint={mapStyles.heatmap}
              //filter={places_filter}
              isLayerChecked={true} />

            {showCircles &&
              <Layer
                id='hidden_dots'
                type='circle'
                minzoom={minZoom}
                maxzoom={20}
                //source-layer='places_data'
                source-layer='heatmap'
                beforeId='road-label'
                paint={mapStyles.hidden_circles}
                //filter={places_filter}
                isLayerChecked={true} />
            }

            <Layer
              id='places_markers'
              key='places_markers'
              minzoom={minZoom}
              maxzoom={20}
              source-layer='heatmap'
              type='symbol'
              beforeId='road-label'
              layout={mapStyles.marker_layout}
              paint={mapStyles.marker_paint} />
          </Source>
        }

        {has_places_data &&
          <Source
            id='places_data'
            type="geojson"
            data={places_layer_geojson}
            cluster={false}>

            <Layer
              id='heatmap'
              type='heatmap'
              minzoom={minZoom}
              maxzoom={20}
              source='places_data'
              beforeId='transit_routes'
              paint={mapStyles.heatmap}
              //filter={places_filter}
              isLayerChecked={true} />

            {true && <Layer
                id='places_markers'
                key='places_markers'
                //beforeId='photo_markers'
                minzoom={minZoom}
                maxzoom={20}
                type='symbol'
                beforeId='road-label'
                //beforeId='slow street icon'
                layout={mapStyles.marker_layout}
                paint={mapStyles.marker_paint} />
            }

          </Source>
        }

        {has_route_data &&
          <Source
            id='route'
            type="geojson"
            data={routeData}>

            <Layer
              id="walking_route"
              type="line"
              layout={mapStyles.route_layout}
              paint={routeStyle}
            />

            <Layer
              id="walking_route_arrow"
              type="symbol"
              layout={mapStyles.route_arrow_layout}
              paint={routeArrowStyle}
            />

          </Source>
        }

        {showTransit &&
          <>
            <Source
              id='transit_routes_simplified'
              type="vector"
              url="mapbox://stevepepple.ckappip0l014h2bqhfy4djcbx-9334t" />

            <Layer
              id='transit_routes_simplified'
              //beforeId='markers'
              type='line'
              source='transit_routes_simplified'
              source-layer='transit_routes_simplified'
              paint={mapStyles.transit_paint}
            />
          </>
        }

        {/* Only render popup if it's not null */}
        {popupInfo &&
          <Popup
            //tipSize={1}
            anchor="bottom"
            className={'marker-popup'}
            offset={popupInfo.isMarkerImage ? 40 : 10}
            //offsetTop={popupInfo.isMarkerImage ? -60 : -10}
            longitude={popupInfo.longitude}
            latitude={popupInfo.latitude}
            closeButton={popupInfo.close}
            closeOnMove={false}>
            {popupInfo.name}
            {popupInfo?.image ? <img src={popupInfo?.image} style={popUpStyle} /> : null}
          </Popup>
        }

        {/* Map accepts child layers */}
        <div className="children">
          {props.children}
        </div>

        {isMobile == false
          ? <ScaleControl />
          : null }

      </ReactMapGL>

      {showExplore &&
        <a className="explore-map" href={openInApp ? exploreLink : 'map'}>Explore Map</a>
      }
    </div>
  )
}

Map.defaultProps = {
  loading: false,
  link: null,
  openInApp: true,
  boundsToMarkers: false,
  dragPan: true,
  dragPanEnabled: true, // Override drag behavior to handle card touch events
  heatmap: false,
  loadFromTile: false,
  mapOptions: {
    interactive: true
  },
  markerStyle: 'icons',
  map3d: false,
  offsetTop: 0,
  offsetFilterHeight: 0,
  showDraggableMarker: false,
  showExplore: true,
  showFullScreen: true,
  showCircles: false,
  showEvents: true, // TODO: set from filter state if All or Events is selected
  showHeat: true,
  showMarkers: true, // TODO: Make it more clear what is photo markers vs place markers
  showMarkerLabel: true,
  showPhotoMarkers: true,
  showPlaces: true,
  showTextOnly: false,
  showTransit: false,
  placesLayer: [],
  width: 420,
  bearing: 0,
  pitch: 2,
  //latitude: 37.7749295,
  //longitude: -122.4194155,
  scrollZoom: false,
  showCards: true, // TODO: Update from Wordpress options
  zoom: mapStyles.MAPBOX_ZOOM_DEFAULT,
}

//Map.whyDidYouRender = true
export default React.memo(Map);